/**
 * lightbox.js
 * Standalone per-variant image lightbox for Shopify custom order page.
 * 
 * USAGE:
 *   1. Upload this file to Shopify Admin → Settings → Files
 *      OR place in your theme: Assets → lightbox.js
 *
 *   2. In your Liquid template, load it before </body>:
 *      <script src="{{ 'lightbox.js' | asset_url }}" defer></script>
 *
 *   3. Make sure your Liquid outputs:
 *      - <a class="previewImage" href="#" data-variant-id="{{ variant.id }}">
 *          <img src="..." alt="...">
 *        </a>
 *      - <div class="variant-image-data" data-variant-id="{{ variant.id }}" style="display:none;">
 *          (one <span> per image attached to this variant)
 *          <span data-src="{{ image | img_url: '1024x' }}" data-alt="{{ image.alt }}"></span>
 *        </div>
 *
 *   4. The lightbox HTML and CSS are injected automatically — no extra markup needed.
 */

(function () {
  'use strict';

  // ── State ─────────────────────────────────────────────
  let images       = [];   // current group: [{ src, alt }, ...]
  let currentIndex = 0;
  let overlay, imgEl, captionEl, counterEl, closeBtn, prevBtn, nextBtn;

  // ── Inject HTML ───────────────────────────────────────
  function injectHTML() {
    const html = `
      <div class="lb-overlay" id="lbOverlay" role="dialog" aria-modal="true" aria-label="Image viewer">
        <button class="lb-close"  id="lbClose"  aria-label="Close">&times;</button>
        <button class="lb-prev"   id="lbPrev"   aria-label="Previous image"><svg width="15" height="24" viewBox="0 0 15 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M5.67568 12L15 2.8L12.1622 0L4.85858e-07 12L12.1622 24L15 21.2L5.67568 12Z" fill="white"/>
</svg></button>
        <div class="lb-inner">
          <img class="lb-img" id="lbImg" src="" alt="">
        </div>
        <button class="lb-next"   id="lbNext"   aria-label="Next image"><svg width="15" height="24" viewBox="0 0 15 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M9.32432 12L0 2.8L2.83784 0L15 12L2.83784 24L0 21.2L9.32432 12Z" fill="white"/>
</svg></button>
        <div class="lb-caption"  id="lbCaption"></div>
        <div class="lb-counter"  id="lbCounter"></div>
      </div>
    `;
    const wrapper = document.createElement('div');
    wrapper.innerHTML = html.trim();
    document.body.appendChild(wrapper.firstChild);
  }

  // ── Inject CSS ────────────────────────────────────────
  function injectCSS() {
    const css = `
      /* ── Overlay ── */
      .lb-overlay {
        display: none;
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.88);
        z-index: 99999;
        align-items: center;
        justify-content: center;
      }
      .lb-overlay.active {
        display: flex;
      }

      /* ── Image container ── */
      .lb-inner {
        position: relative;
        max-width: 90vw;
        max-height: 90vh;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .lb-img {
        max-width: 82vw;
        max-height: 82vh;
        object-fit: contain;
        border-radius: 6px;
        box-shadow: 0 8px 48px rgba(0, 0, 0, 0.7);
        background: #fff;
        transition: opacity 0.18s ease;
      }
      .lb-img.loading {
        opacity: 0.35;
      }

      /* ── Close button ── */
      .lb-close {
        position: fixed;
        top: 18px;
        right: 26px;
        font-size: 42px;
        line-height: 1;
        color: #fff;
        background: none;
        border: none;
        cursor: pointer;
        z-index: 100000;
        padding: 0;
        transition: color 0.15s;
      }
      .lb-close:hover { color: #ccc; }

      /* ── Prev / Next arrows ── */
      .lb-prev,
      .lb-next {
        position: fixed;
        top: 50%;
        transform: translateY(-50%);
        font-size: 56px;
        line-height: 1;
        color: #fff;
        background: none;
        border: none;
        cursor: pointer;
        z-index: 100000;
        padding: 0 16px;
        user-select: none;
        transition: color 0.15s;
        align-items: center;
        height: 47px;
        background-color: #21305E;
      }
      .lb-prev { left: 8px; }
      .lb-next { right: 8px; }
      .lb-prev:hover,
      .lb-next:hover { color: #ccc; }

      /* ── Caption ── */
      .lb-caption {
        position: fixed;
        width: 400px;
        bottom: 80px;
        left: 50%;
        transform: translateX(-50%);
        color: #1a2452;
        font-size: 14px;
        text-align: center;
        z-index: 100000;
        white-space: nowrap;
        max-width: 80vw;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      /* ── Counter ── */
      .lb-counter {
        position: fixed;
        bottom: 22px;
        left: 50%;
        transform: translateX(-50%);
        color: rgba(255, 255, 255, 0.65);
        font-size: 13px;
        letter-spacing: 1px;
        z-index: 100000;
      }

      /* ── Thumbnail dots ── */
      .lb-dots {
        position: fixed;
        bottom: 70px;
        left: 50%;
        transform: translateX(-50%);
        display: flex;
        gap: 8px;
        z-index: 100000;
      }
      .lb-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: rgba(255,255,255,0.4);
        cursor: pointer;
        transition: background 0.2s;
        border: none;
        padding: 0;
      }
      .lb-dot.active {
        background: #fff;
      }

      /* ── Touch hint (mobile) ── */
      @media (max-width: 768px) {
        .lb-caption {
        color: #eee;
      }
        .lb-prev { left: 2px; font-size: 40px; }
        .lb-next { right: 2px; font-size: 40px; }
        .lb-img  { max-width: 94vw; max-height: 75vh; }
      }
    `;
    const style = document.createElement('style');
    style.id = 'lb-styles';
    style.textContent = css;
    document.head.appendChild(style);
  }

  // ── Get variant images from hidden data divs ──────────
  function getVariantImages(variantId) {
    const dataEl = document.querySelector(
      `.variant-image-data[data-variant-id="${variantId}"]`
    );

    if (dataEl) {
      const spans = dataEl.querySelectorAll('span[data-src]');
      if (spans.length) {
        return Array.from(spans).map(span => ({
          src: span.dataset.src,
          alt: span.dataset.alt || ''
        }));
      }
    }

    // Fallback: use the visible thumbnail, upscaled to 1024x
    const thumbLink = document.querySelector(
      `a.previewImage[data-variant-id="${variantId}"] img`
    );
    if (thumbLink) {
      const largeSrc = thumbLink.src.replace(
        /_(pico|icon|thumb|small|compact|medium|large|grande|original|master)(\.\w+)(\?|$)/,
        '_1024x$2$3'
      );
      return [{ src: largeSrc, alt: thumbLink.alt || '' }];
    }

    return [];
  }

  // ── Open lightbox for a variant ───────────────────────
  function openLightbox(variantId) {
    const variantImages = getVariantImages(variantId);
    if (!variantImages.length) return;

    images       = variantImages;
    currentIndex = 0;

    renderDots();
    showImage(0);

    overlay.classList.add('active');
    document.body.style.overflow = 'hidden';

    // Focus trap: put focus on close button
    closeBtn.focus();
  }

  // ── Close lightbox ────────────────────────────────────
  function closeLightbox() {
    overlay.classList.remove('active');
    document.body.style.overflow = '';
    imgEl.src = '';
  }

  // ── Show image at index ───────────────────────────────
  function showImage(index) {
    currentIndex = ((index % images.length) + images.length) % images.length;
    const entry  = images[currentIndex];

    // Loading fade
    imgEl.classList.add('loading');
    const temp  = new Image();
    temp.onload = () => {
      imgEl.src = entry.src;
      imgEl.alt = entry.alt;
      imgEl.classList.remove('loading');
    };
    temp.onerror = () => {
      imgEl.classList.remove('loading');
    };
    temp.src = entry.src;

    captionEl.textContent = entry.alt;
    counterEl.textContent = (currentIndex + 1) + ' / ' + images.length;

    // Show/hide arrows
    const showArrows = images.length > 1;
    prevBtn.style.display = showArrows ? 'flex' : 'none';
    nextBtn.style.display = showArrows ? 'flex' : 'none';

    // Update dots
    const dots = overlay.querySelectorAll('.lb-dot');
    dots.forEach((dot, i) => {
      dot.classList.toggle('active', i === currentIndex);
    });
  }

  // ── Render thumbnail dots ─────────────────────────────
  function renderDots() {
    // Remove old dots container if exists
    const existing = overlay.querySelector('.lb-dots');
    if (existing) existing.remove();

    if (images.length <= 1) return;

    const dotsEl = document.createElement('div');
    dotsEl.className = 'lb-dots';

    images.forEach((_, i) => {
      const dot = document.createElement('button');
      dot.className = 'lb-dot' + (i === 0 ? ' active' : '');
      dot.setAttribute('aria-label', 'Go to image ' + (i + 1));
      dot.addEventListener('click', (e) => {
        e.stopPropagation();
        showImage(i);
      });
      dotsEl.appendChild(dot);
    });

    overlay.appendChild(dotsEl);
  }

  // ── Touch / swipe support ─────────────────────────────
  let touchStartX = 0;
  let touchEndX   = 0;

  function handleTouchStart(e) {
    touchStartX = e.changedTouches[0].screenX;
  }

  function handleTouchEnd(e) {
    touchEndX = e.changedTouches[0].screenX;
    const diff = touchStartX - touchEndX;
    if (Math.abs(diff) > 50) {
      diff > 0 ? showImage(currentIndex + 1) : showImage(currentIndex - 1);
    }
  }

  // ── Bind all events ───────────────────────────────────
  function bindEvents() {
    // Delegated click on previewImage links
    document.addEventListener('click', function (e) {
      const link = e.target.closest('a.previewImage');
      if (!link) return;
      e.preventDefault();
      const variantId = link.dataset.variantId;
      if (variantId) openLightbox(variantId);
    });

    closeBtn.addEventListener('click', closeLightbox);
    prevBtn.addEventListener('click',  () => showImage(currentIndex - 1));
    nextBtn.addEventListener('click',  () => showImage(currentIndex + 1));

    // Click backdrop to close
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) closeLightbox();
    });

    // Keyboard navigation
    document.addEventListener('keydown', function (e) {
      if (!overlay.classList.contains('active')) return;
      switch (e.key) {
        case 'ArrowLeft':  showImage(currentIndex - 1); break;
        case 'ArrowRight': showImage(currentIndex + 1); break;
        case 'Escape':     closeLightbox();             break;
      }
    });

    // Swipe support
    overlay.addEventListener('touchstart', handleTouchStart, { passive: true });
    overlay.addEventListener('touchend',   handleTouchEnd,   { passive: true });
  }

  // ── Init ──────────────────────────────────────────────
  function init() {
    injectCSS();
    injectHTML();

    overlay    = document.getElementById('lbOverlay');
    imgEl      = document.getElementById('lbImg');
    captionEl  = document.getElementById('lbCaption');
    counterEl  = document.getElementById('lbCounter');
    closeBtn   = document.getElementById('lbClose');
    prevBtn    = document.getElementById('lbPrev');
    nextBtn    = document.getElementById('lbNext');

    bindEvents();
  }

  // Run after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();